import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useGetProductsQuery,
  useGetProductSuppliersQuery,
} from '@app/api/Products';
import {
  useGetClientServiceProductUsagesQuery,
  useGetWarehousesQuery,
} from '@app/api/TechnologicalMaps';
import { useUserPrint } from '@app/hooks';
import type { UseClientServiceProductUsagesEffectsProps } from '@app/pages/Clients/EditClientService/components/ClientServiceProductUsages/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

export const useClientServiceProductUsagesEffects = ({
  serviceId,
}: UseClientServiceProductUsagesEffectsProps) => {
  const [query] = useSearchParams();
  const [queryServiceId] = useState(Number(query.get('id')));

  const memoizedServiceId = useMemo(
    () => serviceId || queryServiceId,
    [queryServiceId, serviceId]
  );

  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const {
    data: clientServiceProductUsagesData,
    error: clientServiceProductUsagesError,
    isLoading: isClientServiceProductUsagesLoading,
  } = useGetClientServiceProductUsagesQuery(
    {
      ActiveBranchId,
      clientServiceId: memoizedServiceId,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const productIds =
    clientServiceProductUsagesData?.map(({ ProductId }) => ProductId) ?? [];

  const {
    data: productsData,
    isLoading: isProductsLoading,
    error: productsError,
  } = useGetProductsQuery(
    { ActiveBranchId, includeIds: productIds },
    { skip: isClientServiceProductUsagesLoading }
  );

  const warehouseIds =
    clientServiceProductUsagesData?.map(({ WarehouseId }) => WarehouseId) ?? [];

  const {
    data: warehousesData,
    error: warehousesError,
    isLoading: isWarehousesLoading,
  } = useGetWarehousesQuery(
    { ActiveBranchId, warehouseIds },
    { skip: isClientServiceProductUsagesLoading }
  );

  const {
    data: suppliers,
    isLoading: isLoadingSuppliers,
    error: errorSuppliers,
  } = useGetProductSuppliersQuery({ ActiveBranchId });

  useEffect(() => {
    if (productsError) {
      showNotification(handleCatchError(productsError));
    }
  }, [productsError, showNotification]);

  useEffect(() => {
    if (warehousesError) {
      showNotification(handleCatchError(warehousesError));
    }
  }, [warehousesError, showNotification]);

  useEffect(() => {
    if (clientServiceProductUsagesError) {
      showNotification(handleCatchError(clientServiceProductUsagesError));
    }
  }, [clientServiceProductUsagesError, showNotification]);

  useEffect(() => {
    if (errorSuppliers) {
      showNotification(handleCatchError(errorSuppliers));
    }
  }, [errorSuppliers, showNotification]);

  const isFetchingData =
    isProductsLoading ||
    isWarehousesLoading ||
    isClientServiceProductUsagesLoading ||
    isLoadingSuppliers;

  return {
    productsData,
    warehousesData,
    clientServiceProductUsagesData,
    isFetchingData,
    suppliers,
    memoizedServiceId,
  };
};
